import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUG_STATUSES, BUG_SEVERITIES, statusBadgeClass, priorityBadgeClass } from "@/lib/constants";
import { Search, Calendar, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination from "@/components/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";

export default function BugsPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sevFilter, setSevFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { items: bugs, total, page, pageSize, setPage, setPageSize } = usePaginatedList("/bugs", {
    q,
    status: statusFilter === "all" ? "" : statusFilter,
    severity: sevFilter === "all" ? "" : sevFilter,
    project_id: projectFilter === "all" ? "" : projectFilter,
    assignee_id: assigneeFilter === "all" ? "" : assigneeFilter,
    from_date: fromDate,
    to_date: toDate,
  });

  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([api.get("/users/all"), api.get("/projects/all")]);
        setUsers(u.data);
        setProjects(p.data);
      } catch {}
    })();
  }, []);

  const userName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  const resetFilters = () => {
    setQ("");
    setStatusFilter("all");
    setSevFilter("all");
    setProjectFilter("all");
    setAssigneeFilter("all");
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="space-y-6" data-testid="bugs-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Bugs</h1>
        <p className="text-sm text-muted-foreground mt-1">Defects across the entire organization.</p>
      </div>

      <div className="bg-card border border-border p-3.5 rounded-lg shadow-sm space-y-3">
        <div className="flex flex-wrap gap-2.5 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search bugs by title, ID, or description…"
              className="pl-9 h-9 text-xs"
              data-testid="bugs-search"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="bugs-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {BUG_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sevFilter} onValueChange={setSevFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="bugs-filter-severity">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {BUG_SEVERITIES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="bugs-filter-project">
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="bugs-filter-assignee">
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignees</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(q || statusFilter !== "all" || sevFilter !== "all" || projectFilter !== "all" || assigneeFilter !== "all" || fromDate || toDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/40 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>Reported Date:</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">From:</span>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">To:</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
            />
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-3 py-3 font-semibold text-center w-12">S.No</th>
                <th className="px-3 py-3 font-semibold w-24">Bug ID</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Project</th>
                <th className="px-3 py-3 font-semibold">Severity</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Assignee & Tagged</th>
                <th className="px-3 py-3 font-semibold text-center">Est (h)</th>
                <th className="px-3 py-3 font-semibold text-center">Reopens</th>
                <th className="px-3 py-3 font-semibold">Reported</th>
              </tr>
            </thead>
            <tbody>
              {bugs.map((b, i) => (
                <tr
                  key={b.id}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer"
                  data-testid={`bug-list-row-${b.id}`}
                  onClick={() => b.project_id && navigate(`/projects/${b.project_id}?tab=bugs&bugId=${b.id}`)}
                >
                  <td className="px-3 py-3 font-mono text-xs text-muted-foreground text-center">
                    {(page - 1) * pageSize + i + 1}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs font-semibold text-primary">
                    #b-{b.id ? b.id.slice(-6) : ""}
                  </td>
                  <td className="px-4 py-3 font-medium">{b.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">{projectName(b.project_id)}</td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={priorityBadgeClass(b.severity)}>{b.severity}</Badge>
                  </td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={statusBadgeClass(b.status)}>{b.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-medium text-foreground">{userName(b.assignee_id)}</div>
                    {b.tagged_user_ids && b.tagged_user_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {b.tagged_user_ids.map((uid) => (
                          <Badge key={uid} variant="secondary" className="text-[10px] py-0 px-1 font-normal">
                            @{userName(uid).split(" ")[0]}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-center">{b.estimated_hours ?? "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-center">
                    {b.reopen_count > 0 ? (
                      <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                        {b.reopen_count}×
                      </Badge>
                    ) : "0"}
                  </td>
                  <td className="px-3 py-3 text-xs font-mono text-muted-foreground">
                    {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
              {bugs.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">
                    No bugs match the current filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="bugs-pagination"
        />
      </Card>
    </div>
  );
}
